import { useCallback, useEffect, useRef, useState } from 'react';
import {
    GoogleReCaptchaProvider,
    useGoogleReCaptcha,
} from 'react-google-recaptcha-v3';
import Button from 'components/Button';
import {
    fetchResearchOffer,
    fetchResearchStrip,
    reserveResearchSponsorship,
} from 'api/research-sponsorship';
import {
    forgetReservation,
    priceLabel,
    researchBoxView,
    reserveErrorMessage,
    storeReservation,
    storedReservation,
} from 'utils/research-sponsor';
import styles from 'styles/modules/newsAlert/ResearchSponsor.module.scss';

import { news_alerts } from 'lang/en.json';

const copy = news_alerts.research_sponsor;

// The API only stores web addresses; this keeps any other scheme out of an href.
const webHref = (url) => (/^https?:\/\//i.test(url || '') ? url : null);

// Paid credit, so the link says so to search engines. Shown at the top of the alert and
// under the story byline, where the box promises the sponsor's name.
export const ResearchCredit = ({ name, website }) => {
    const href = webHref(website);
    return (
        <p className={styles.credit}>
            <span className={styles.creditLabel}>{copy.sold}</span>{' '}
            {href ? (
                <a
                    href={href}
                    target="_blank"
                    rel="sponsored nofollow noopener"
                >
                    {name}
                </a>
            ) : (
                <strong>{name}</strong>
            )}
        </p>
    );
};

// The price under an open alert's headline, so experts who never scroll to the box see
// it (Asana 1220157993244795). Asked for on mount: the cached page doesn't carry it.
// `loginHref`: where a logged-out visitor goes, since the box is for members only.
export const ResearchStrip = ({ alertId, loginHref, deadline }) => {
    const [strip, setStrip] = useState(null);
    const [ended, setEnded] = useState(false);

    useEffect(() => {
        fetchResearchStrip(alertId)
            .then(setStrip)
            .catch(() => {});
    }, [alertId]);

    // The countdown closes the alert only if it ticks on the exact second, which a
    // throttled background tab can miss. Over ~24 days setTimeout overflows and fires at once.
    useEffect(() => {
        const left = new Date(deadline) - Date.now();
        setEnded(left <= 0);
        if (!(left > 0 && left < 2 ** 31 - 1)) return;
        const timer = setTimeout(() => setEnded(true), left);
        return () => clearTimeout(timer);
    }, [deadline]);

    if (!strip?.price || ended) return null;

    const toBox = (event) => {
        const box = document
            .getElementById('research-sponsor')
            ?.closest('aside');
        if (!box) return;
        event.preventDefault();
        box.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        // The price must stay out of Google's snippets and AI answers.
        <div className={styles.strip} data-nosnippet>
            <span>
                {copy.strip} <strong>{priceLabel(strip.price)}+VAT</strong>
            </span>
            <a
                className={[
                    'button',
                    'button__primary',
                    styles.stripButton,
                ].join(' ')}
                href={loginHref || '#research-sponsor'}
                onClick={loginHref ? undefined : toBox}
            >
                {loginHref ? copy.strip_more_login : copy.strip_more}
            </a>
        </div>
    );
};

const EMPTY = { sponsorName: '', website: '', email: '' };
const GONE = ['RESERVED', 'SOLD', 'NOT_OFFERED'];
const RESUME_WAIT_MS = 10000;

// `resume`: the buyer's stored reservation with what they last sent, so going
// back to their checkout needs no second form.
const ReserveForm = ({ alertId, resume, onRefresh }) => {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [fields, setFields] = useState(resume?.fields ?? EMPTY);
    const [showForm, setShowForm] = useState(!resume?.fields);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const resumed = useRef(false);

    const reserve = async (values) => {
        setLoading(true);
        setError(null);
        try {
            const result = await reserveResearchSponsorship(alertId, {
                sponsorName: values.sponsorName,
                website: values.website.trim() || null,
                email: values.email,
                recaptchaToken: await executeRecaptcha('research_sponsor'),
                reservationToken:
                    storedReservation(alertId)?.reservationToken || undefined,
            });
            if (result?.url) {
                storeReservation(alertId, result.reservationToken, values);
                window.location.assign(result.url);
                return;
            }
            // This buyer's earlier checkout had already gone through.
            forgetReservation(alertId);
            onRefresh();
        } catch (err) {
            setError(reserveErrorMessage(err?.response));
            setShowForm(true);
            if (GONE.includes(err?.response?.data?.code)) {
                forgetReservation(alertId);
                onRefresh();
            }
        }
        setLoading(false);
    };

    // The reCAPTCHA script loads after mount; resume as soon as it is ready.
    useEffect(() => {
        if (!resume?.fields || !executeRecaptcha || resumed.current) return;
        resumed.current = true;
        reserve(resume.fields);
    }, [executeRecaptcha]);

    // A script that never loads (blocked, offline) must not leave the buyer waiting.
    useEffect(() => {
        if (!resume?.fields) return;
        const timer = setTimeout(() => {
            if (resumed.current) return;
            resumed.current = true;
            setError(copy.spam_check_slow);
            setShowForm(true);
        }, RESUME_WAIT_MS);
        return () => clearTimeout(timer);
    }, []);

    const set = (field) => (e) =>
        setFields({ ...fields, [field]: e.target.value });

    const submit = (e) => {
        e.preventDefault();
        if (!executeRecaptcha) {
            setError(copy.form_loading);
            return;
        }
        reserve(fields);
    };

    if (!showForm) {
        return (
            <p className={styles.small} role="status">
                {copy.resuming}
            </p>
        );
    }

    return (
        <form className={styles.form} onSubmit={submit}>
            <label className={styles.field}>
                {copy.name}
                <input
                    required
                    maxLength={120}
                    autoComplete="organization"
                    value={fields.sponsorName}
                    onChange={set('sponsorName')}
                />
            </label>
            <label className={styles.field}>
                {copy.website}
                <input
                    maxLength={500}
                    inputMode="url"
                    autoComplete="url"
                    placeholder="https://"
                    value={fields.website}
                    onChange={set('website')}
                />
            </label>
            <label className={styles.field}>
                {copy.email}
                <input
                    required
                    type="email"
                    maxLength={255}
                    autoComplete="email"
                    value={fields.email}
                    onChange={set('email')}
                />
            </label>
            {error && (
                <p className={styles.error} role="alert">
                    {error}
                </p>
            )}
            <Button title={copy.submit} theme={'primary'} loading={loading} />
        </form>
    );
};

// The offer is for logged-in experts only, so the cached alert page never carries
// it: the box asks for it on mount and whenever the tab comes back, and shows
// nothing until the API answers with one.
const ResearchSponsorBox = ({ alertId }) => {
    const [live, setLive] = useState(null);
    const [stored, setStored] = useState(null);
    // 'form' or 'resume' once the buyer has asked; reCAPTCHA loads only then.
    const [open, setOpen] = useState(null);

    const refresh = useCallback(() => {
        setStored(storedReservation(alertId));
        fetchResearchOffer(alertId)
            .then(setLive)
            .catch(() => {});
    }, [alertId]);

    useEffect(() => {
        refresh();
        const onVisible = () => {
            if (document.visibilityState === 'visible') refresh();
        };
        document.addEventListener('visibilitychange', onVisible);
        return () =>
            document.removeEventListener('visibilitychange', onVisible);
    }, [refresh]);

    // A sale is credited at the top of the page from the public data.
    const view = researchBoxView(live, Boolean(stored));
    const shown = Boolean(view) && view !== 'sold';

    // The email's button lands here after the login; the box only exists once the
    // offer has loaded, so the browser's own jump to #research-sponsor misses it.
    useEffect(() => {
        if (shown && window.location.hash === '#research-sponsor') {
            document
                .getElementById('research-sponsor')
                ?.closest('aside')
                ?.scrollIntoView();
        }
    }, [shown]);

    if (!shown) return null;

    return (
        // The sales pitch must not become the search snippet for the alert.
        <aside
            className={styles.box}
            aria-labelledby="research-sponsor"
            data-nosnippet
        >
            <h2 id="research-sponsor" className={styles.heading}>
                {copy.heading}
            </h2>
            <p className={styles.text}>{live.researchDetail}</p>
            <p className={styles.includes}>{copy.includes}</p>
            <ul className={styles.list}>
                {copy.benefits.map((item) => (
                    <li key={item}>{item}</li>
                ))}
            </ul>
            <p>
                <strong>
                    {copy.cost_label} {priceLabel(live.price)}
                </strong>{' '}
                {copy.cost}
            </p>
            <p className={styles.small}>{copy.decline}</p>

            {view === 'reserved' && (
                <p className={styles.reserved} role="status">
                    {copy.reserved}
                </p>
            )}
            {view === 'available' && !open && (
                <Button
                    title={stored?.fields ? copy.resume : copy.open}
                    theme={'primary'}
                    onClick={() => setOpen(stored?.fields ? 'resume' : 'form')}
                />
            )}
            {view === 'available' && open && (
                <GoogleReCaptchaProvider
                    reCaptchaKey={process.env.GOOGLE_RECAPTCHA_SITE_TOKEN}
                >
                    <ReserveForm
                        alertId={alertId}
                        resume={open === 'resume' ? stored : null}
                        onRefresh={refresh}
                    />
                </GoogleReCaptchaProvider>
            )}
        </aside>
    );
};

export default ResearchSponsorBox;
